import { FunctionComponent, memo, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import Skeleton from 'react-loading-skeleton';
import { ConfigContext, UserContext } from '../../../../context';
import {
  CardOpenButton,
  FileBlockData,
  FileLink,
  FileStatus,
  ImageGallery,
  ImageGalleryItem,
  MediaBlockUploadPreview,
  useFreeLoginPopup,
} from '../../../../shared';

import classes from './File.module.scss';

export const File: FunctionComponent<FileBlockData> = memo(({ items, isPdf }) => {
  const { t } = useTranslation();

  const { freeLoginModal, freeLoginHandler } = useFreeLoginPopup();

  const { isAnonymous } = useContext(UserContext).userProfile;

  const { actionsDisabled = true } = { ...useContext(ConfigContext).config.elements.freeLogin };

  const fileClickHandler = useCallback(() => {
    if (isAnonymous && actionsDisabled) {
      freeLoginHandler();
      return;
    }

    window.open(items?.[0]?.file?.url, '_blank', 'noopener, noreferrer');
  }, [actionsDisabled, freeLoginHandler, isAnonymous, items]);

  if (!items.length) {
    return null;
  }

  const { file, previewUrl, caption, copyright } = items[0];

  if (!file) {
    return null;
  }

  const { url, status } = file;

  if (status === FileStatus.PROCESSING) {
    return <Skeleton height={'3rem'} />;
  }

  const type = isPdf ? 'pdf' : 'file';

  if (!url) {
    return (
      <MediaBlockUploadPreview iconId={type} label={t(`richTextEditor.${type}-placeholder`)} />
    );
  }

  const imageGalleryItems: ImageGalleryItem[] = [
    { image: { url: previewUrl, rightholder: copyright } },
  ];

  return (
    <>
      <div className={classes['file']} onClick={fileClickHandler}>
        <CardOpenButton label={caption || file.originalFilename} />

        <ImageGallery items={imageGalleryItems} singleImageFullSize />
        <FileLink file={file} caption={caption} />
      </div>

      {freeLoginModal}
    </>
  );
});
